import type { BookingWithDetails, Customer, Service, Staff, WorkingHours } from "@shared/schema";
import {
  parsePagedList,
  unwrapPagedList,
  type ListLinks,
  type ListMetadata,
} from "./bookingApi";
import { sameEntityId } from "./utils";

/** Keep in sync with `ADMIN_LIST_PAGE_SIZE` / `ADMIN_LIST_MAX_PAGE_SIZE` in `queryClient.ts`. */
const ADMIN_SERVICES_DEFAULT_PAGE_SIZE = 20;
const ADMIN_SERVICES_MAX_PAGE_SIZE = 200;

export type { ListLinks, ListMetadata };

/**
 * API placeholder rows may share id `0` or empty; several distinct rows can legitimately use it.
 * Do not treat such ids as unique keys for deduplication.
 */
export function isPlaceholderEntityId(id: string): boolean {
  return id === "" || id === "0" || Number(id) === 0;
}

/** Preset for `NewBookingDrawer` (new booking, rebook, or edit existing). */
export type NewBookingDrawerPreset = {
  serviceId?: string;
  staffId?: string;
  customerId?: string;
  date?: string;
  time?: string;
  /** When set, submit uses PATCH to update this booking instead of POST. */
  editingBookingId?: string;
  /**
   * Snapshot from the booking row for review UI — matches card/detail (`resolveOfferingName` / `formatCents`).
   * Catalog rows can disagree (e.g. duplicate service ids, HQ placeholders).
   */
  reviewOfferingName?: string;
  reviewPriceCents?: number | null;
  reviewCurrency?: string | null;
  /** When the bookings list API omits customer id — prefill edit drawer from the row. */
  prefillCustomer?: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
  };
};

export function bookingToEditPreset(b: BookingWithDetails): NewBookingDrawerPreset {
  const start = new Date(b.startAt);
  const date = start.toISOString().slice(0, 10);
  const time = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
  const customerId = b.customerId?.trim();
  const nameFromRow = b.service?.name?.trim();
  const priceFromRow = b.service?.priceCents ?? b.priceCents ?? null;
  const currencyFromRow = b.currency ?? b.service?.currency ?? null;

  return {
    editingBookingId: b.id,
    serviceId: b.serviceId,
    staffId: b.staffId ?? undefined,
    customerId: customerId || undefined,
    date,
    time,
    reviewOfferingName: nameFromRow || undefined,
    reviewPriceCents: priceFromRow,
    reviewCurrency: currencyFromRow,
    ...(!customerId && b.customer
      ? {
          prefillCustomer: {
            firstName: b.customer.firstName,
            lastName: b.customer.lastName,
            email: b.customer.email,
            phone: b.customer.phone,
          },
        }
      : {}),
  };
}

/** @deprecated Prefer `normalizeBookingsListPayload` + OpenAPI `ListMetadata`. */
export type PaginatedBookings = {
  data: BookingWithDetails[];
  meta?: { total: number; page: number; limit: number; totalPages: number };
};

function splitCustomerName(full: string): { firstName: string; lastName: string } {
  const t = full.trim();
  if (!t) return { firstName: "", lastName: "" };
  const i = t.indexOf(" ");
  if (i === -1) return { firstName: t, lastName: "" };
  return { firstName: t.slice(0, i), lastName: t.slice(i + 1).trim() };
}

function isFlatAdminBookingRow(row: unknown): boolean {
  if (!row || typeof row !== "object") return false;
  const r = row as Record<string, unknown>;
  if (r.customer && typeof r.customer === "object" && r.customer !== null) return false;
  return "customerEmail" in r || "customerName" in r;
}

function syntheticService(
  businessId: string,
  serviceId: string,
  priceCents: number,
  currency: string | null,
): Service {
  return {
    id: serviceId,
    businessId,
    name: "",
    description: null,
    category: null,
    durationMins: 60,
    bufferBeforeMins: null,
    bufferAfterMins: null,
    priceCents,
    currency,
    depositEnabled: null,
    depositType: null,
    depositValue: null,
    depositPolicyText: null,
    active: true,
  };
}

function mapFlatAdminBooking(raw: Record<string, unknown>): BookingWithDetails {
  const businessId = String(raw.businessId ?? "");
  const serviceIdStr = String(raw.serviceId ?? "");
  const staffIdRaw = raw.staffId;
  const staffId = staffIdRaw == null || staffIdRaw === "" ? null : String(staffIdRaw);

  const customerEmail = String(raw.customerEmail ?? "");
  const customerName = String(raw.customerName ?? "");
  const { firstName, lastName } = splitCustomerName(customerName);

  const customerIdFromApi = raw.customerId != null && raw.customerId !== "" ? String(raw.customerId) : "";

  const customer: Customer = {
    id: customerIdFromApi || `legacy-email:${customerEmail}`,
    businessId,
    firstName,
    lastName,
    email: customerEmail,
    phone: raw.customerPhone != null ? String(raw.customerPhone) : null,
    notes: null,
    createdAt: null,
  };

  const priceCents = typeof raw.priceCents === "number" ? raw.priceCents : null;
  const currency = raw.currency != null ? String(raw.currency) : null;

  const serviceNameFromApi =
    raw.serviceName != null && String(raw.serviceName).trim() !== "" ? String(raw.serviceName).trim() : "";
  const serviceBase = syntheticService(businessId, serviceIdStr, priceCents ?? 0, currency);
  const service: Service = { ...serviceBase, name: serviceNameFromApi || serviceBase.name };

  const staffNameFromApi =
    raw.staffName != null && String(raw.staffName).trim() !== "" ? String(raw.staffName).trim() : "";

  const staffMember: Staff | null =
    staffId === null
      ? null
      : {
          id: staffId,
          businessId,
          displayName: staffNameFromApi,
          email: null,
          phone: null,
          avatarUrl: null,
          role: null,
          bio: null,
          active: true,
        };

  return {
    id: String(raw.id),
    businessId,
    serviceId: serviceIdStr,
    staffId,
    customerId: customerIdFromApi,
    startAt: String(raw.startAt),
    endAt: String(raw.endAt),
    status: typeof raw.status === "string" ? raw.status.toLowerCase() : "",
    notes: raw.notes != null ? String(raw.notes) : null,
    manageToken: String(raw.manageToken ?? ""),
    priceCents,
    currency,
    stripeSessionId:
      raw.stripeCheckoutSessionId != null
        ? String(raw.stripeCheckoutSessionId)
        : raw.stripeSessionId != null
          ? String(raw.stripeSessionId)
          : null,
    stripePaymentIntentId: raw.stripePaymentIntentId != null ? String(raw.stripePaymentIntentId) : null,
    depositAmountCents: typeof raw.depositAmountCents === "number" ? raw.depositAmountCents : null,
    paymentStatus: raw.paymentStatus != null ? String(raw.paymentStatus).toLowerCase() : null,
    source: raw.source != null ? String(raw.source).toLowerCase() : null,
    createdAt: raw.createdAt != null ? String(raw.createdAt) : null,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : null,
    service,
    staffMember,
    customer,
  };
}

function normalizeNestedBookingRow(row: BookingWithDetails): BookingWithDetails {
  /** Flat `mapFlatAdminBooking` stringifies ids; nested JSON rows often keep numeric `staffId` / `serviceId`, which breaks `===` filters (e.g. calendar staff). */
  const staffIdRaw = row.staffId as unknown;
  const staffId =
    staffIdRaw == null || staffIdRaw === "" ? null : String(staffIdRaw);
  /** Nested `customer` may be OpenAPI `CustomerResponse` (`name` only) — map to UI `Customer`. */
  const customer = row.customer ? mapCustomerResponseToCustomer(row.customer as unknown) : row.customer;
  return {
    ...row,
    id: String(row.id ?? ""),
    businessId: String(row.businessId ?? ""),
    serviceId: String(row.serviceId ?? ""),
    staffId,
    customerId: String(row.customerId ?? ""),
    customer,
    service: row.service
      ? { ...row.service, id: String(row.service.id ?? "") }
      : row.service,
    staffMember: row.staffMember
      ? { ...row.staffMember, id: String(row.staffMember.id ?? "") }
      : row.staffMember,
    status: typeof row.status === "string" ? row.status.toLowerCase() : row.status,
    source: row.source != null ? String(row.source).toLowerCase() : row.source,
  };
}

function mapBookingRowToWithDetails(row: unknown): BookingWithDetails {
  if (isFlatAdminBookingRow(row)) {
    return mapFlatAdminBooking(row as Record<string, unknown>);
  }
  return normalizeNestedBookingRow(row as BookingWithDetails);
}

/**
 * Full admin bookings list response: rows plus `metadata` / `_links` when the API sends `PagedListResponse`.
 */
export function normalizeBookingsListPayload(json: unknown): {
  bookings: BookingWithDetails[];
  metadata: ListMetadata | null;
  links: ListLinks | null;
} {
  const { items, metadata, links } = parsePagedList(json);
  return {
    bookings: items.map(mapBookingRowToWithDetails),
    metadata,
    links,
  };
}

export function normalizeBookingsList(json: unknown): BookingWithDetails[] {
  return normalizeBookingsListPayload(json).bookings;
}

/**
 * Whether a booking from GET `/admin/bookings` belongs to a profile from GET `/admin/customers`.
 * List rows may omit `customerId` or use `legacy-email:…` on nested `customer` while the CRM row has a real UUID;
 * the detail page uses `/customers/{id}/bookings` — this aligns list counts and “last visit” with that behaviour.
 */
export function bookingMatchesAdminCustomer(b: BookingWithDetails, c: Customer): boolean {
  if (sameEntityId(b.customerId, c.id)) return true;
  if (b.customer && sameEntityId(b.customer.id, c.id)) return true;
  const bookingEmail = (b.customer?.email ?? "").trim().toLowerCase();
  const profileEmail = (c.email ?? "").trim().toLowerCase();
  if (bookingEmail && profileEmail && bookingEmail === profileEmail) return true;
  return false;
}

/**
 * GET `/v1/booking/admin/customers` (and `?q=` search): paged `{ data }` or plain array.
 * Each row is OpenAPI `CustomerResponse` (`name`); map to UI `Customer` (`firstName` / `lastName`).
 */
export function normalizeAdminCustomersListPayload(raw: unknown): Customer[] {
  let rows: unknown[] = [];
  if (Array.isArray(raw)) rows = raw;
  else if (raw && typeof raw === "object" && "data" in raw && Array.isArray((raw as { data: unknown }).data)) {
    rows = (raw as { data: unknown[] }).data;
  }
  return rows.map((item) => mapCustomerResponseToCustomer(item));
}

/**
 * Offering title for UI: use nested `service.name` when present, otherwise match `serviceId`
 * against GET admin services (list rows may omit names; v1 also exposes `serviceName` on booking rows).
 */
export function resolveOfferingName(
  booking: Pick<BookingWithDetails, "serviceId" | "service">,
  services?: Service[] | null,
): string | undefined {
  const fromRow = booking.service?.name?.trim();
  if (fromRow) return fromRow;
  if (booking.serviceId == null || booking.serviceId === "") return undefined;
  const hit = services?.find((s) => String(s.id) === String(booking.serviceId));
  return hit?.name?.trim() || undefined;
}

/**
 * Staff display name for UI: use nested `staffMember.displayName` when present, otherwise match `staffId`
 * against GET /api/admin/staff (list API rows often omit nested staff).
 */
export function resolveStaffDisplayName(
  booking: Pick<BookingWithDetails, "staffId" | "staffMember">,
  staff?: Staff[] | null,
): string | undefined {
  const fromRow = booking.staffMember?.displayName?.trim();
  if (fromRow) return fromRow;
  if (booking.staffId == null || booking.staffId === "") return undefined;
  const hit = staff?.find((s) => String(s.id) === String(booking.staffId));
  return hit?.displayName?.trim() || undefined;
}

/** Maps API `ServiceResponse` (`desc`, integer `id`, `staffIds` integers) to UI `Service`. Accepts a raw array or paged `{ data }`. */
export function normalizeAdminServicesResponse(data: unknown): Service[] {
  const rows = unwrapPagedList(data);
  return rows.map((item) => {
    const s = item as Record<string, unknown>;
    const description =
      s.description != null ? String(s.description) : s.desc != null ? String(s.desc) : null;
    const rawStaffIds = s.staffIds;
    const staffIds = Array.isArray(rawStaffIds)
      ? rawStaffIds.map((x) => String(x))
      : null;
    const rawProd =
      s.eposNowProductId ??
      (s as Record<string, unknown>).epos_now_product_id ??
      (s as Record<string, unknown>).EposNowProductId;
    const eposNowProductId =
      rawProd != null && rawProd !== ""
        ? Number(rawProd)
        : null;
    return {
      ...(s as unknown as Service),
      id: String(s.id ?? ""),
      description,
      staffIds,
      eposNowProductId:
        eposNowProductId != null && !Number.isNaN(eposNowProductId) ? eposNowProductId : null,
    };
  });
}

/**
 * Maps `StaffResponse.workingHours` or GET `/staff/{id}/hours` hour rows to UI `WorkingHours`.
 * (Swagger: `StaffWorkingHourResponse` — no per-row id; we synthesize stable keys for React.)
 */
export function mapStaffWorkingHourApiRowsToUi(
  staffId: string,
  staffRowIndex: number,
  raw: unknown,
): WorkingHours[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((w, j) => {
    const row = w as Record<string, unknown>;
    const dow = typeof row.dayOfWeek === "number" ? row.dayOfWeek : Number(row.dayOfWeek ?? 0);
    return {
      id: `${staffId}-${staffRowIndex}-${dow}-${j}`,
      staffId,
      dayOfWeek: dow,
      startTime: String(row.startTime ?? "09:00"),
      endTime: String(row.endTime ?? "17:00"),
      isActive: row.isActive !== false,
    };
  });
}

/** Maps admin GET `/v1/booking/admin/staff` `StaffResponse` rows to UI `Staff`. Accepts a raw array or paged `{ data }`. */
export function normalizeAdminStaffListResponse(data: unknown): Staff[] {
  const rows = unwrapPagedList(data);
  return rows.map((item, index) => {
    const s = item as Record<string, unknown>;
    const rawEpos = s.eposNowId;
    const eposNowId =
      rawEpos != null && rawEpos !== ""
        ? Number(rawEpos)
        : null;
    const idStr = String(s.id ?? "");
    const rawWh = s.workingHours;
    const workingHours = mapStaffWorkingHourApiRowsToUi(
      idStr,
      index,
      Array.isArray(rawWh) ? rawWh : [],
    );
    return {
      ...(s as unknown as Staff),
      id: idStr,
      eposNowId: eposNowId != null && !Number.isNaN(eposNowId) ? eposNowId : null,
      workingHours,
    };
  });
}

/**
 * Shallow-clone `data` so React Query cache never holds a shared array reference that a buggy client/API
 * mutates across page requests (symptom: 20 + 20 + 20 rows when changing pages).
 */
export function clonePagedListResponseData(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const o = raw as Record<string, unknown>;
  const d = o.data;
  if (!Array.isArray(d)) return raw;
  return { ...o, data: d.slice() };
}

/** Services list including `metadata` / `_links` when present. */
export function normalizeAdminServicesListPayload(data: unknown): {
  services: Service[];
  metadata: ListMetadata | null;
  links: ListLinks | null;
} {
  const { items, metadata, links } = parsePagedList(data);
  const rows = Array.isArray(items) ? items.slice() : [];
  /** Only dedupe real service ids. Placeholder rows use id `0` (several may appear); never collapse those. */
  const seenRealIds = new Set<string>();
  const unique: unknown[] = [];
  for (const row of rows) {
    const idRaw = (row as Record<string, unknown>).id;
    const id = idRaw == null ? "" : String(idRaw);
    if (!isPlaceholderEntityId(id)) {
      if (seenRealIds.has(id)) continue;
      seenRealIds.add(id);
    }
    unique.push(row);
  }
  /**
   * One page only: some APIs omit `metadata.limit` or return a growing `data` array. Never show more
   * than a single page (default 20, max 200) so the offerings list cannot stack multiple pages.
   */
  const limitFromMeta = metadata?.limit;
  const effectiveCap =
    typeof limitFromMeta === "number" && limitFromMeta > 0
      ? Math.min(limitFromMeta, ADMIN_SERVICES_MAX_PAGE_SIZE)
      : ADMIN_SERVICES_DEFAULT_PAGE_SIZE;
  const capped =
    unique.length > effectiveCap ? unique.slice(0, effectiveCap) : unique;
  return { services: normalizeAdminServicesResponse(capped), metadata, links };
}

/** Staff list including `metadata` / `_links` when present. */
export function normalizeAdminStaffListPayload(data: unknown): {
  staff: Staff[];
  metadata: ListMetadata | null;
  links: ListLinks | null;
} {
  const { items, metadata, links } = parsePagedList(data);
  return { staff: normalizeAdminStaffListResponse(items), metadata, links };
}

/** Inclusive row range for the current page (e.g. "1–20 of 45"). */
export function computeAdminListRange(
  metadata: ListMetadata | null | undefined,
  rowsOnPage: number,
): { start: number; end: number; total: number } | null {
  const total = metadata?.totalRecords;
  if (total == null || total <= 0 || rowsOnPage <= 0) return null;
  const page = Math.max(1, metadata?.page ?? 1);
  const limit = metadata?.limit ?? rowsOnPage;
  const start = (page - 1) * limit + 1;
  const end = Math.min(start + rowsOnPage - 1, total);
  return { start, end, total };
}

export type CustomerDetailResponse = {
  customer: Customer & { tags?: string[] | null; marketingConsent?: boolean | null };
  bookings: BookingWithDetails[];
};

/**
 * Maps GET `/v1/booking/admin/customers/{id}` `CustomerResponse` (single `name` field) to UI `Customer`.
 */
export function mapCustomerResponseToCustomer(raw: unknown): Customer {
  if (!raw || typeof raw !== "object") {
    return {
      id: "",
      businessId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: null,
      notes: null,
      createdAt: null,
    };
  }
  const r = raw as Record<string, unknown>;
  const legacyFirst = r.firstName != null ? String(r.firstName).trim() : "";
  const legacyLast = r.lastName != null ? String(r.lastName).trim() : "";
  const fullName = r.name != null ? String(r.name).trim() : "";
  let firstName = legacyFirst;
  let lastName = legacyLast;
  if (!legacyFirst && !legacyLast && fullName) {
    const i = fullName.indexOf(" ");
    firstName = i === -1 ? fullName : fullName.slice(0, i).trim();
    lastName = i === -1 ? "" : fullName.slice(i + 1).trim();
  }
  return {
    id: String(r.id ?? ""),
    businessId: String(r.businessId ?? ""),
    firstName,
    lastName,
    email: String(r.email ?? ""),
    phone: r.phone != null && String(r.phone).trim() !== "" ? String(r.phone) : null,
    notes: r.notes != null ? String(r.notes) : null,
    createdAt: r.createdAt != null ? String(r.createdAt) : null,
  };
}
